/** Display quotes for eAuditor Premium seats. Billing must return this charge. */

export const MIN_SEATS = 1
export const MAX_SEATS = 999

export const MONTHLY_PRICE_CENTS = 1299
export const ANNUAL_EQUIVALENT_MONTHLY_CENTS = 999
export const ANNUAL_PRICE_PER_SEAT_CENTS = ANNUAL_EQUIVALENT_MONTHLY_CENTS * 12
export const ANNUALIZED_MONTHLY_CENTS = MONTHLY_PRICE_CENTS * 12
export const ANNUAL_SAVINGS_PER_SEAT_CENTS =
    ANNUALIZED_MONTHLY_CENTS - ANNUAL_PRICE_PER_SEAT_CENTS

export const normalizeSeatCount = value => {
    if (typeof value === 'number') {
        if (!Number.isInteger(value)) return null
        if (value < MIN_SEATS || value > MAX_SEATS) return null
        return value
    }
    const raw = String(value ?? '').trim()
    if (!/^\d+$/.test(raw)) return null
    const seats = Number(raw)
    if (!Number.isInteger(seats) || seats < MIN_SEATS || seats > MAX_SEATS) {
        return null
    }
    return seats
}

export const quoteLicense = (seats, frequency) => {
    const count = normalizeSeatCount(seats)
    if (!count) return null
    const annual = frequency === 'annual'
    const unitCents = annual ? ANNUAL_PRICE_PER_SEAT_CENTS : MONTHLY_PRICE_CENTS
    return {
        seats: count,
        frequency: annual ? 'annual' : 'monthly',
        billingLabel: annual ? 'Annual' : 'Monthly',
        unitCents,
        totalCents: unitCents * count,
        equivalentMonthlyCents: annual
            ? ANNUAL_EQUIVALENT_MONTHLY_CENTS
            : MONTHLY_PRICE_CENTS,
        savingsPerSeatCents: ANNUAL_SAVINGS_PER_SEAT_CENTS,
    }
}

export const formatMoney = cents =>
    (Number(cents) / 100).toLocaleString('en-US', {
        style: 'currency',
        currency: 'USD',
    })

export const formatPerPeriod = (cents, frequency) => {
    const suffix = frequency === 'annual' ? '/year' : '/month'
    return `${formatMoney(cents)}${suffix}`
}

const moneyToCents = value => {
    if (value == null || value === '') return null
    const amount = Number(value)
    if (!Number.isFinite(amount)) return null
    return Math.round(amount * 100)
}

const firstCents = (order, keys) => {
    if (!order || typeof order !== 'object') return null
    let found = null
    keys.forEach(key => {
        if (found != null) return
        const value = order[key]
        if (value == null || value === '') return
        const cents = moneyToCents(value)
        if (cents != null) found = cents
    })
    return found
}

const centsClose = (left, right) => Math.abs(left - right) <= 1

/**
 * Accept a billing response only when the list price matches the seat
 * formula. A server discount is accepted when Subtotal minus DiscountAmount
 * plus Tax equals TotalDueToday. A client total is never the charge.
 */
export const serverPricingIsValid = (order, expectedCents) => {
    const total = firstCents(order, [
        'ChargeAmount',
        'chargeAmount',
        'PaymentAmount',
        'paymentAmount',
        'TotalDueToday',
        'totalDueToday',
        'RecurringAmount',
        'recurringAmount',
    ])
    const subtotal = firstCents(order, [
        'Subtotal',
        'subtotal',
        'PlanAmount',
        'planAmount',
    ])
    const tax = firstCents(order, ['TaxAmount', 'taxAmount', 'Tax', 'tax'])
    const discount = firstCents(order, ['DiscountAmount', 'discountAmount'])
    const discountCents = discount != null && discount > 0 ? discount : 0
    const empty = {
        ok: false,
        reason: 'missing',
        total: null,
        subtotal: null,
        tax: null,
        discount: null,
    }
    if (total == null) return empty

    const taxCents = tax ?? 0
    const payableFrom = listPrice => listPrice - discountCents + taxCents

    if (subtotal != null) {
        if (!centsClose(subtotal, expectedCents) || discountCents > subtotal) {
            return {
                ok: false,
                reason: 'mismatch',
                total,
                subtotal,
                tax,
                discount: discountCents || null,
            }
        }
        if (!centsClose(payableFrom(subtotal), total)) {
            return {
                ok: false,
                reason: discountCents ? 'discount' : 'tax',
                total,
                subtotal,
                tax,
                discount: discountCents || null,
            }
        }
        return {
            ok: true,
            reason: 'match',
            total,
            subtotal,
            tax,
            discount: discountCents || null,
        }
    }

    const listPrice = total - taxCents + discountCents
    if (!centsClose(listPrice, expectedCents) || discountCents > listPrice) {
        return {
            ok: false,
            reason: 'mismatch',
            total,
            subtotal: null,
            tax,
            discount: discountCents || null,
        }
    }
    return {
        ok: true,
        reason: 'match',
        total,
        subtotal: expectedCents,
        tax,
        discount: discountCents || null,
    }
}

const firstValue = (order, keys) => {
    if (!order || typeof order !== 'object') return null
    let found = null
    keys.forEach(key => {
        if (found != null && found !== '') return
        const value = order[key]
        if (value == null || value === '') return
        found = value
    })
    return found
}

/** Flatten Cart/Quote and Cart/Checkout into the fields the checkout already reads. */
export const asCartOrder = raw => {
    if (typeof raw === 'number') return { ChargeAmount: raw }
    let order = raw
    if (order && typeof order === 'object') {
        order =
            order.Data ||
            order.data ||
            order.Result ||
            order.result ||
            order
    }
    if (!order || typeof order !== 'object') return order
    const nestedQuote = order.Quote || order.quote
    if (nestedQuote && typeof nestedQuote === 'object') {
        order = { ...nestedQuote, ...order }
    }
    const charge = firstValue(order, [
        'ChargeAmount',
        'chargeAmount',
        'PaymentAmount',
        'paymentAmount',
        'Total',
        'total',
        'AmountDue',
        'amountDue',
        'GrandTotal',
        'grandTotal',
        'TotalDueToday',
        'totalDueToday',
        'RecurringAmount',
        'recurringAmount',
        'ExpectedTotal',
        'expectedTotal',
    ])
    return {
        ...order,
        ChargeAmount: order.ChargeAmount ?? order.chargeAmount ?? charge,
        Subtotal:
            order.Subtotal ??
            order.subtotal ??
            order.PlanAmount ??
            order.planAmount ??
            null,
        TaxAmount:
            order.TaxAmount ??
            order.taxAmount ??
            order.Tax ??
            order.tax ??
            null,
        CartOrderId:
            order.CartOrderId ??
            order.cartOrderId ??
            order.OrderNo ??
            order.orderNo ??
            null,
        PaymentOrderId:
            order.PaymentOrderId ??
            order.paymentOrderId ??
            order.PaymentIntentId ??
            order.paymentIntentId ??
            null,
        ClientSecretId:
            order.ClientSecretId ??
            order.clientSecretId ??
            order.ClientSecret ??
            order.clientSecret ??
            null,
    }
}

export const pickPremiumPlan = (plans, frequency) => {
    const list = Array.isArray(plans) ? plans : []
    const buyable = list.filter(plan => {
        const flag = plan?.BuyAvailable ?? plan?.buyAvailable
        return flag === true || flag === 1 || flag === 'true'
    })
    const nameOf = plan =>
        String(plan?.PlanName || plan?.planName || '').toLowerCase()
    const annualName = plan => /year|annual/.test(nameOf(plan))
    const premiumPool = buyable.filter(plan => {
        const name = nameOf(plan)
        return !name.includes('free') && !name.includes('enterprise')
    })
    const pool = premiumPool.length
        ? premiumPool
        : buyable.filter(plan => !nameOf(plan).includes('free'))
    if (!pool.length) return null
    if (frequency === 'annual') {
        return pool.find(annualName) || pool[0]
    }
    return pool.find(plan => !annualName(plan)) || pool[0]
}

export const readStripeKey = data => {
    const list = Array.isArray(data)
        ? data
        : data?.Items || data?.items || data?.Plans || data?.plans || []
    const keyFromRoot =
        data?.StripeClientKey ||
        data?.stripeClientKey ||
        data?.StripePublishableKey ||
        data?.PublishableKey ||
        data?.Config?.StripeClientKey ||
        data?.config?.StripeClientKey ||
        ''
    const keyFromPlan = list.find(
        plan =>
            plan?.StripeClientKey ||
            plan?.stripeClientKey ||
            plan?.StripePublishableKey ||
            plan?.PublishableKey,
    )
    return (
        keyFromRoot ||
        keyFromPlan?.StripeClientKey ||
        keyFromPlan?.stripeClientKey ||
        keyFromPlan?.StripePublishableKey ||
        keyFromPlan?.PublishableKey ||
        ''
    )
}

export const planListFromResponse = data => {
    if (Array.isArray(data)) return data
    return data?.Items || data?.items || data?.Plans || data?.plans || []
}
